import { Link } from "react-router-dom";
import Layout from "../Layout";
import SEOHead from "../SEOHead";
import ServiceSchema from "../ServiceSchema";
import HeroSection from "./HeroSection";          // From same folder (as requested)
import fabricationHero from "../assets/mezzanine flooring.png"; // Adjust if needed

const FabricationServices = () => {
  const capabilities = [
    "MS & SS Structural Fabrication",
    "Truss Fabrication",
    "Mezzanine Floor Structures",
    "GI Frame Construction",
    "Gate & Railing Fabrication",
    "Industrial Platforms & Walkways",
    "Custom Steel Component Fabrication",
  ];

  const features = [
    {
      title: "High-End Machinery & Skilled Workforce",
      description: "Ensuring consistency and accuracy in every part we produce.",
    },
    {
      title: "Custom Fabrication",
      description: "Every project is built according to specific dimensions and needs.",
    },
    {
      title: "Strong & Durable Finishing",
      description: "Anti-corrosion coating and industrial-grade paint ensure longevity.",
    },
    {
      title: "On-Time Delivery",
      description: "Fast fabrication process to support your project deadlines.",
    },
    {
      title: "Quality Assurance",
      description: "Strict checks at each stage to ensure structural safety.",
    },
  ];

  return (
    <Layout>
      <SEOHead
        title="Fabrication Services Near Me | TR Structures – Hyderabad"
        description="TR Structures provides expert fabrication services near you in Hyderabad, including structural fabrication, trusses, mezzanine floors, GI frames, gates, and custom steel solutions."
        keywords="fabrication services, fabrication services near me, structural fabrication, steel fabrication services, trusses fabrication, mezzanine floors, GI frames, custom steel fabrication, fabrication services Hyderabad"
      />
      <ServiceSchema
        serviceType="Structural Fabrication Services"
        description="MS/SS structural fabrication, trusses, mezzanine floors, GI frames, gates, railings and custom steel components."
      />

      <main>
        <HeroSection
          title="Fabrication Services"
          subtitle="Precision Steel Fabrication"
          description="Complete fabrication solutions using MS, SS, GI Sheets, Structural Steel, and FRP materials for every industrial requirement."
          backgroundImage={fabricationHero}
          primaryButtonText="Get Quote"
          primaryButtonLink="/enquiry"
          secondaryButtonText="Contact Us"
          secondaryButtonLink="/contact"
        />

        {/* Main Content */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="animate-slide-in-left">
                <img
                  src={fabricationHero}
                  alt="Fabrication Services"
                  className="rounded-xl shadow-2xl w-full object-cover"
                />
              </div>

              <div className="animate-slide-in-right">
                <h2 className="font-montserrat text-3xl md:text-4xl font-bold text-foreground mb-6">
                  Precision at <span className="text-secondary">Every Stage</span>
                </h2>
                <p className="text-muted-foreground mb-4">
                  Fabrication is not just about joining steel — it is about understanding the project's purpose, load requirements, and long-term usage. Our experienced technicians and engineers work closely to ensure each fabricated component meets industrial quality standards.
                </p>
                <p className="text-muted-foreground mb-6">
                  We handle everything from custom frames, beams, columns, trusses, mezzanine floors, fencing systems, industrial supports, machine platforms, and more.
                </p>

                <h3 className="font-montserrat text-xl font-bold text-foreground mb-4">
                  Our Fabrication Capabilities:
                </h3>
                <div className="space-y-2">
                  {capabilities.map((cap) => (
                    <div key={cap} className="flex items-center gap-3 text-muted-foreground">
                      <svg
                        className="w-5 h-5 text-secondary flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      {cap}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="section-padding bg-muted">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="font-montserrat text-3xl md:text-4xl font-bold text-foreground mb-4">
                Why Our Fabrication Service <span className="text-primary">Stands Out</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <div
                  key={feature.title}
                  className={`p-6 bg-card rounded-xl shadow-lg card-hover animate-slide-up stagger-${index + 1}`}
                  style={{ animationFillMode: "both" }}
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <svg
                      className="w-6 h-6 text-primary"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <h3 className="font-montserrat text-xl font-bold text-foreground mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-padding bg-secondary">
          <div className="container-custom text-center">
            <h2 className="font-montserrat text-3xl md:text-4xl font-bold text-secondary-foreground mb-4">
              Reliable Fabrication Begins Here
            </h2>
            <p className="text-secondary-foreground/90 text-lg mb-8 max-w-2xl mx-auto">
              From small-scale fabrication work to large industrial projects, TR Structures is your one-stop solution for all types of steel fabrication services in Hyderabad.
            </p>
            <Link to="/enquiry" className="btn-primary inline-block">
              Request a Quote
            </Link>
          </div>
        </section>
      </main>
    </Layout>
  );
};

export default FabricationServices;